/**
 * Progressive, same-group navigation. The server remains the source of each URL.
 * Only marked links in the existing house/policy navigation are intercepted.
 * No fetched script is executed and no header, cart, account or footer is replaced.
 */
const ROOT_SELECTOR = '[data-b21-navigation]';
const STATE_KEY = 'b21Navigation';
const MAX_ENTRIES = 12;
const CACHE_MAX_AGE = 5 * 60 * 1000;

/** @param {URL} url */
const routeKey = (url) => `${url.origin}${url.pathname}${url.search}`;
/** @param {unknown} state */
const mergeableState = (state) => state && typeof state === 'object' && !Array.isArray(state) ? state : {};
/** @param {HTMLElement} root */
const contextKey = (root) => [root.dataset.group, root.dataset.locale, root.dataset.market, root.dataset.version].join('|');

/** @typedef {{pane: HTMLElement, title: string, canonical: string, template: string|null, savedAt: number}} PageRecord */

export class BouchilinoGroupNavigation {
  /** @type {HTMLElement} */
  #root;
  /** @type {HTMLElement} */
  #pane;
  /** @type {HTMLElement|null} */
  #status;
  /** @type {Map<string, PageRecord>} */
  #cache = new Map();
  /** @type {Set<string>} */
  #routes = new Set();
  #controller = new AbortController();
  /** @type {AbortController|null} */
  #request = null;
  #sequence = 0;
  #scrollFrame = 0;
  #focusFrame = 0;
  #timeout = 0;
  #currentKey;
  #currentHash;
  #context;
  #previousRestoration;
  #disposed = false;
  #fallbackStarted = false;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.#root = root;
    this.#pane = /** @type {HTMLElement} */ (root.querySelector('[data-b21-pane]'));
    this.#status = root.querySelector('[data-b21-status]');
    this.#context = contextKey(root);
    const current = new URL(location.href);
    this.#currentKey = routeKey(current);
    this.#currentHash = current.hash;

    for (const link of root.querySelectorAll('[data-b21-nav] a[data-b21-link][href]')) {
      const url = new URL(link.getAttribute('href'), location.href);
      if (url.origin === location.origin) this.#routes.add(routeKey(url));
    }
    // A direct product enquiry may carry a query that is absent from the general nav.
    this.#routes.add(this.#currentKey);
    this.#cache.set(this.#currentKey, this.#record(document, this.#pane));
    this.#previousRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    root.dataset.b21Ready = 'true';
    const options = { signal: this.#controller.signal };
    document.addEventListener('click', this.#onClick, options);
    window.addEventListener('popstate', this.#onPopState, options);
    window.addEventListener('scroll', this.#onScroll, { ...options, passive: true });
    window.addEventListener('pagehide', this.#onPageHide, options);
    window.addEventListener('pageshow', this.#onPageShow, options);
    document.addEventListener('b21:fragment-change', this.#onFragmentChange, options);
    window.addEventListener('hashchange', this.#onFragmentChange, options);
    this.#saveScroll();
    this.#syncLinks(current);
    this.#updateReturnTo(current);
  }

  get root() { return this.#root; }

  dispose() {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#sequence += 1;
    this.#request?.abort();
    this.#controller.abort();
    cancelAnimationFrame(this.#scrollFrame);
    cancelAnimationFrame(this.#focusFrame);
    clearTimeout(this.#timeout);
    this.#cache.clear();
    this.#root.removeAttribute('aria-busy');
    delete this.#root.dataset.b21Ready;
    history.scrollRestoration = this.#previousRestoration;
  }

  /** @param {Document} doc @param {HTMLElement} pane @returns {PageRecord} */
  #record(doc, pane) {
    return {
      pane,
      title: doc.title,
      canonical: doc.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
      template: doc.querySelector('#MainContent')?.getAttribute('data-template') || null,
      savedAt: Date.now(),
    };
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-b21-link][href], a[data-b21-footer-link][href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const isNavLink = this.#root.contains(link) && Boolean(link.closest('[data-b21-nav]')) && link.hasAttribute('data-b21-link');
    const footerGroup = link.closest('footer [data-b21-footer-group]')?.getAttribute('data-b21-footer-group');
    const isSameGroupFooterLink = footerGroup === this.#root.dataset.group;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self') || link.getAttribute('rel')?.includes('external')) return;
    const url = new URL(link.href);
    if ((!isNavLink && !isSameGroupFooterLink) || !this.#routes.has(routeKey(url)) || url.origin !== location.origin) {
      // A native footer departure wins immediately, even when its document is
      // slower than a pending partial response. Leave the browser click intact.
      if (url.origin === location.origin && routeKey(url) !== this.#currentKey) {
        this.#sequence += 1;
        this.#request?.abort();
        clearTimeout(this.#timeout);
        cancelAnimationFrame(this.#focusFrame);
        this.#setBusy(false);
      }
      return;
    }
    // Native modal semantics must remain in charge while the drawer is open.
    if (history.state?.cartDrawerOpen || document.querySelector('dialog[open]')) return;
    event.preventDefault();
    this.#saveScroll();
    void this.#navigate(url, false, null);
  };

  /** @param {PopStateEvent} event */
  #onPopState = (event) => {
    const url = new URL(location.href);
    if (routeKey(url) === this.#currentKey && url.hash === this.#currentHash) {
      // First mobile Back only closes the cart. Never move focus or push an entry.
      return;
    }
    if (!this.#routes.has(routeKey(url))) {
      this.#fallback(url, true);
      return;
    }
    void this.#navigate(url, true, event.state?.[STATE_KEY] || null);
  };

  #onScroll = () => {
    if (!this.#scrollFrame) this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#saveScroll();
    });
  };

  #onPageHide = () => {
    this.#saveScroll();
    this.#sequence += 1;
    this.#request?.abort();
    this.#setBusy(false);
    clearTimeout(this.#timeout);
  };

  #onPageShow = () => {
    // A bfcache restore preserves the instance and its form nodes, not another mount.
    this.#fallbackStarted = false;
    history.scrollRestoration = 'manual';
    this.#updateReturnTo(new URL(location.href));
  };

  #onFragmentChange = () => {
    const url = new URL(location.href);
    if (routeKey(url) !== this.#currentKey) return;
    this.#currentHash = url.hash;
    this.#syncLinks(url);
    this.#updateReturnTo(url);
    this.#saveScroll();
  };

  #saveScroll() {
    if (this.#disposed || routeKey(new URL(location.href)) !== this.#currentKey || history.state?.cartDrawerOpen) return;
    const state = mergeableState(history.state);
    history.replaceState({ ...state, [STATE_KEY]: { context: this.#context, x: scrollX, y: scrollY } }, '');
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#root.toggleAttribute('aria-busy', busy);
    if (busy) this.#root.setAttribute('aria-busy', 'true');
    if (this.#status) this.#status.textContent = busy ? this.#root.dataset.loadingLabel || 'Loading section…' : '';
    for (const link of this.#root.querySelectorAll('[data-b21-nav] a[data-b21-link]')) link.removeAttribute('data-b21-pending');
  }

  /** @param {URL} url @param {boolean} isPop @param {{context?:string,x?:number,y?:number}|null} saved */
  async #navigate(url, isPop, saved) {
    const sequence = ++this.#sequence;
    this.#request?.abort();
    cancelAnimationFrame(this.#focusFrame);
    this.#request = new AbortController();
    const signal = this.#request.signal;
    const key = routeKey(url);
    const started = performance.now();
    let cached = false;
    let timedOut = false;
    this.#setBusy(true);
    for (const link of this.#root.querySelectorAll('[data-b21-nav] a[data-b21-link]')) {
      if (routeKey(new URL(link.href)) === key) link.setAttribute('data-b21-pending', '');
    }
    clearTimeout(this.#timeout);
    this.#timeout = window.setTimeout(() => { timedOut = true; this.#request?.abort(); }, 15000);

    try {
      let record = this.#cache.get(key);
      // Form DOM is retained for the document lifetime, including values and listeners.
      if (record && !record.pane.querySelector('form') && Date.now() - record.savedAt > CACHE_MAX_AGE && key !== this.#currentKey) {
        this.#cache.delete(key);
        record = undefined;
      }
      cached = Boolean(record);
      if (!record) {
        const response = await fetch(key, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Invalid section response');
        const responseUrl = new URL(response.url || key);
        if (routeKey(responseUrl) !== key) throw new Error('Unexpected section redirect');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incomingRoot = doc.querySelector(ROOT_SELECTOR);
        const incomingPane = incomingRoot?.querySelector('[data-b21-pane]');
        if (!(incomingRoot instanceof HTMLElement) || !(incomingPane instanceof HTMLElement)
          || contextKey(incomingRoot) !== this.#context
          || incomingRoot.querySelectorAll('[data-b21-pane]').length !== 1
          || !incomingPane.textContent.trim() || !incomingPane.querySelector('h1')) throw new Error('Missing section context');
        if (doc.documentElement.lang !== document.documentElement.lang || doc.documentElement.dir !== document.documentElement.dir) throw new Error('Locale changed');
        // Reject executable markup; do not sanitize or mutate the approved privacy HTML.
        if (incomingPane.querySelector('script,iframe,object,embed,base,meta[http-equiv]')) throw new Error('Unsupported embedded content');
        for (const element of [incomingPane, ...incomingPane.querySelectorAll('*')]) {
          for (const attribute of element.attributes) {
            if (/^on/i.test(attribute.name) || ((attribute.name === 'href' || attribute.name === 'src') && /^\s*javascript:/i.test(attribute.value))) throw new Error('Executable attribute');
          }
        }
        record = this.#record(doc, incomingPane);
        const canonical = new URL(record.canonical, key);
        if (!record.canonical || canonical.origin !== location.origin) throw new Error('Invalid canonical');
      }
      if (signal.aborted || sequence !== this.#sequence || this.#disposed) return;

      // Preserve the old pane until the new, validated document is ready.
      const previousPane = this.#pane;
      const nextPane = record.pane;
      const state = mergeableState(history.state);
      if (!isPop && url.href !== location.href) {
        history.pushState({ ...state, [STATE_KEY]: { context: this.#context, x: 0, y: 0 } }, '', url.href);
      }
      this.#currentKey = key;
      this.#currentHash = url.hash;
      this.#pane = nextPane;
      if (previousPane !== nextPane) previousPane.replaceWith(nextPane);
      this.#cache.set(key, record);
      this.#trimCache();
      document.title = record.title;
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical && record.canonical) canonical.setAttribute('href', record.canonical);
      const main = document.getElementById('MainContent');
      if (main && record.template !== null) main.setAttribute('data-template', record.template);
      this.#syncLinks(url);
      this.#updateReturnTo(url);
      this.#setBusy(false);
      clearTimeout(this.#timeout);
      if (this.#status) this.#status.textContent = this.#root.dataset.loadedLabel
        || (this.#root.dataset.locale === 'ar' ? 'تم عرض القسم.' : 'Section loaded.');
      document.dispatchEvent(new CustomEvent('b21:content-ready', { detail: { root: this.#root, pane: nextPane, url: url.href, group: this.#root.dataset.group, cached } }));
      if (!cached) this.#wireContactForm(nextPane);
      this.#focusFrame = requestAnimationFrame(() => {
        this.#focusFrame = 0;
        if (sequence !== this.#sequence || this.#disposed) return;
        const restored = isPop && saved?.context === this.#context && Number.isFinite(saved.x) && Number.isFinite(saved.y);
        if (restored) {
          window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
        } else {
          this.#focusDestination(url, !isPop);
        }
        this.#saveScroll();
        document.dispatchEvent(new CustomEvent('b21:navigation-complete', { detail: { group: this.#root.dataset.group, cached, duration: performance.now() - started, isPop } }));
      });
    } catch (error) {
      if (sequence !== this.#sequence || this.#disposed || (signal.aborted && !timedOut)) return;
      this.#setBusy(false);
      if (this.#status) this.#status.textContent = this.#root.dataset.errorLabel || 'Unable to load this section. Opening the full page…';
      document.dispatchEvent(new CustomEvent('b21:navigation-fallback', { detail: { group: this.#root.dataset.group, reason: timedOut ? 'timeout' : 'response' } }));
      this.#fallback(url, isPop);
    } finally {
      if (sequence === this.#sequence) clearTimeout(this.#timeout);
    }
  }

  #trimCache() {
    if (this.#cache.size <= MAX_ENTRIES) return;
    for (const [key, record] of this.#cache) {
      if (key === this.#currentKey || record.pane.querySelector('form')) continue;
      this.#cache.delete(key);
      if (this.#cache.size <= MAX_ENTRIES) return;
    }
  }

  /** @param {HTMLElement} pane */
  #wireContactForm(pane) {
    for (const form of pane.querySelectorAll('form')) {
      if (form.querySelector('input[name="form_type"]')?.value !== 'contact') continue;
      // Shopify's supported API only; never call hCaptcha internals or submit the form.
      if (typeof window.Shopify?.captcha?.protect === 'function') {
        try { window.Shopify.captcha.protect(form); }
        catch { /* Normal form interaction retains Shopify's native protection fallback. */ }
      }
    }
  }

  /** @param {URL} url */
  #syncLinks(url) {
    const policyKey = this.#pane.querySelector('[data-b20-policy]')?.getAttribute('data-b20-policy');
    const hasPolicyMapping = this.#root.dataset.group === 'policies' && policyKey
      && Array.from(this.#root.querySelectorAll('[data-b21-nav] [data-b21-policy-link]')).some(link => link.dataset.b21PolicyLink === policyKey);
    for (const link of this.#root.querySelectorAll('[data-b21-nav] a[data-b21-link]')) {
      const target = new URL(link.href);
      const active = hasPolicyMapping ? link.dataset.b21PolicyLink === policyKey : target.pathname === url.pathname;
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
    for (const link of document.querySelectorAll('footer a[data-b21-footer-link]')) {
      const target = new URL(link.href);
      const footerGroup = link.closest('[data-b21-footer-group]')?.getAttribute('data-b21-footer-group');
      const active = footerGroup === this.#root.dataset.group && (hasPolicyMapping
        ? link.dataset.b21FooterLink === policyKey
        : target.origin === url.origin && target.pathname === url.pathname);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
  }

  /** @param {URL} url */
  #updateReturnTo(url) {
    for (const input of document.querySelectorAll('form input[name="return_to"]')) {
      const form = input.form;
      if (form && new URL(form.action, location.href).pathname.endsWith('/localization')) input.value = `${url.pathname}${url.search}${url.hash}`;
    }
  }

  /** @param {URL} url @param {boolean} moveFocus */
  #focusDestination(url, moveFocus) {
    let target = null;
    if (url.hash) {
      try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch { /* Invalid fragments use the heading. */ }
    }
    const hasFragmentTarget = target instanceof HTMLElement && this.#pane.contains(target);
    if (!hasFragmentTarget) target = this.#pane.querySelector('[data-b21-heading], h1');
    if (!(target instanceof HTMLElement)) return;
    if (moveFocus) {
      // Legal privacy attributes remain verbatim, including on existing fragment targets.
      const focusTarget = target.closest('[data-native-privacy]') ? this.#pane.querySelector('[data-b21-heading], h1') : target;
      if (focusTarget instanceof HTMLElement) {
        if (!focusTarget.hasAttribute('tabindex') && !focusTarget.matches('a[href],button,input,select,textarea,summary')) focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
    }
    if (hasFragmentTarget) {
      const header = document.querySelector('header-component, #header-group, body > header');
      const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'))
        || header?.getBoundingClientRect().height || 0;
      window.scrollTo({ top: Math.max(0, scrollY + target.getBoundingClientRect().top - headerHeight - 16), left: scrollX, behavior: 'instant' });
    } else {
      // Retain the group's navigation above the focused heading. CSS supplies the
      // sticky header scroll margin without changing the approved content itself.
      const nav = this.#root.querySelector('[data-b21-nav]');
      (nav || target).scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }

  /** @param {URL} url @param {boolean} isPop */
  #fallback(url, isPop) {
    if (this.#fallbackStarted) return;
    this.#fallbackStarted = true;
    this.#request?.abort();
    // Native navigation is outside the click handler, so no fetch/retry loop occurs.
    if (isPop) location.replace(url.href);
    else location.assign(url.href);
  }
}

/** @type {BouchilinoGroupNavigation|null} */
let navigation = null;

export function enhanceGroupNavigation() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (navigation?.root === root) return;
  navigation?.dispose();
  navigation = null;
  if (!(root instanceof HTMLElement) || root.dataset.b21Ready === 'true'
    || !['house', 'policies'].includes(root.dataset.group)
    || root.querySelectorAll('[data-b21-pane]').length !== 1
    || !root.querySelector('[data-b21-nav] a[data-b21-link][href]')) return;
  navigation = new BouchilinoGroupNavigation(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhanceGroupNavigation, { once: true });
else enhanceGroupNavigation();
document.addEventListener('shopify:section:load', enhanceGroupNavigation);
document.addEventListener('shopify:section:unload', () => queueMicrotask(enhanceGroupNavigation));

// The independent contact page keeps localization context without becoming a
// navigable group or intercepting links, history, or the existing contact form.
function syncContactLanguageContext() {
  if (!document.querySelector('[data-b21-contact-page]')) return;
  const returnTo = location.pathname + location.search + location.hash;
  for (const input of document.querySelectorAll('form input[name="return_to"]')) {
    if (input.form && new URL(input.form.action, location.href).pathname.endsWith('/localization')) input.value = returnTo;
  }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncContactLanguageContext, { once: true });
else syncContactLanguageContext();
window.addEventListener('pageshow', syncContactLanguageContext);
window.addEventListener('hashchange', syncContactLanguageContext);
document.addEventListener('submit', (event) => {
  if (event.target instanceof HTMLFormElement && new URL(event.target.action, location.href).pathname.endsWith('/localization')) syncContactLanguageContext();
}, { capture: true });
